import React, { useRef } from 'react';
import styled from 'styled-components';
import { useDebounce } from 'react-use';
import { InlineInputCardWrapper } from '@/features/editor/widgets/ai-component/inputs/shared/shared';
import { RichTextEditor } from '@/features/editor/widgets/custom-widget/inputs/typography/rich-text';
import { DeviceType } from '@/utils/definitions';
import { hash } from '@/utils/cache';

export interface HtmlContentRichTextInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
}

export function HtmlContentRichTextInput({
  id,
  value,
  onChange,
}: HtmlContentRichTextInputProps) {
  const ref = useRef<HTMLDivElement>(null);
  const changesRef = useRef(0);
  const [rawValue, setRawValue] = React.useState(value);

  const onValuesChanged = (val: string) => {
    if (changesRef.current > 0 && rawValue !== val) {
      setRawValue(decodeURIComponent(val));
    }
    changesRef.current += 1;
  };

  useDebounce(
    () => {
      if (rawValue.includes(`background-color`)) {
        const container = document.createElement(`div`);
        container.innerHTML = rawValue;
        container.querySelectorAll(`p *`).forEach((el: HTMLElement) => {
          if (el.style.backgroundColor) {
            el.style.removeProperty(`background-color`);
          }
        });
        onChange(container.innerHTML);
      } else {
        onChange(rawValue);
      }
    },
    1000,
    [rawValue],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onNoopChanged = () => {};

  return (
    <Wrapper className="rich-text">
      <StyledRichTextEditor>
        <RichTextEditor
          idToolbar={`html-content-rich-text-editor-${hash(id)}`}
          defaultValue={encodeURIComponent(rawValue) || ``}
          onChange={onValuesChanged}
          ref={ref}
          spec={{
            noAi: true,
            values: {
              value: encodeURIComponent(rawValue) || ``,
            },
          }}
          handleAiAltText={() => {
            // eslint-disable-next-line no-console
            console.debug(`not implemented`);
          }}
          loading={false}
          device={DeviceType.Mobile}
          customization={undefined}
          onValuesChanged={onNoopChanged}
        />
      </StyledRichTextEditor>
    </Wrapper>
  );
}

const Wrapper = styled(InlineInputCardWrapper)`
  && {
    display: grid;
    width: 100%;
    grid-template-columns: 1fr;
  }
`;

const StyledRichTextEditor = styled.div`
  #toolbar-header-0 {
    padding: 0.5rem !important;

    * {
      font-size: 1.2rem;
    }

    div[class*='ButtonsWrapper'] .content {
      display: none;
    }

    div[class*='LineHeightWrapper'] {
      height: 100%;
    }

    div[class*='InputWrapper'] {
      height: 4rem;

      div[class*='number-input'] {
        box-shadow: none;
        padding: 0 0.8rem 0 0;
        height: 100%;
        justify-items: center;
      }
    }
  }

  .ql-snow.ql-toolbar:after,
  .ql-snow .ql-toolbar:after {
    margin-bottom: 1rem;
  }

  .ql-snow .ql-picker:not(.ql-align) {
    box-shadow: none;
  }

  .ql-container {
    margin-top: 1rem;
    min-height: 16rem;
    max-height: 16rem;
    overflow-y: scroll;
  }

  .ql-font {
    max-width: 26rem;
  }
`;
